import { App, Button, Card, Form, Input, Select } from 'antd'
import { ExperimentOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'

import { DEI_Lgc_Mapped } from '@/views/Conversations/Online/Input/AutoSendWAForEmail'
import { openPopup } from '@/hooks/useEmail'
import { getTemplateListAction, resolveColiSnAction } from '@/actions/HtTemplateActions'

// 小组选项
const DEI_OPTIONS = Object.values(DEI_Lgc_Mapped)
  .filter((d) => d.DEI_DepartmentName && !d.DEI_DepartmentName.includes('待删除'))
  .map((d) => ({ label: d.DEI_DepartmentName, value: d.DEI_SN }))

// 语言选项（取自 DEI_Lgc_Mapped 中非空 DEI_LGC，去重）
const LGC_OPTIONS = Array.from(
  new Map(Object.values(DEI_Lgc_Mapped).filter((d) => d.DEI_LGC).map((d) => [d.DEI_LGC, d.lgc_n])),
).map(([value, label]) => ({ label, value }))

// 测试案例：点击自动填充表单
const TEST_CASES = [
  { label: '报价信·CA260906037', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'CA260906037' } },
  { label: '报价信·GY260903136', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'GY260903136' } },
  { label: '报价信·L260903103', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'L260903103' } },
  { label: '报价信·Leeky260903098', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'Leeky260903098' } },
  { label: '报价信·Wang260903093', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'Wang260903093' } },
  { label: '报价信·L260713283', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'L260713283' } },
  { label: '报价信·LYF260714101', values: { template: 'CH_Quotation', dei_sn: 1, lgc: 1, coli_id: 'LYF260714101' } },
  { label: '确认信·LSQah260814135', values: { template: 'CH_Confirmation', dei_sn: 1, lgc: 1, coli_id: 'LSQah260814135' } },
  { label: '确认信·LSQ260814131', values: { template: 'CH_Confirmation', dei_sn: 1, lgc: 1, coli_id: 'LSQ260814131' } },
]

function HtTemplateManager() {
  const { message } = App.useApp()
  const [form] = Form.useForm()

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(false)

  const fetchTemplates = async () => {
    setLoading(true)
    try {
      const list = await getTemplateListAction()
      setTemplates(list)
    } catch (e) {
      setTemplates([])
      message.error(e?.message || '获取模板列表失败')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchTemplates()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // 模板选项：文件名为 {事业部code}_{类型}.html
  const TEMPLATE_OPTIONS = templates.map((t) => ({ label: `${t.group}_${t.type}`, value: `${t.group}_${t.type}` }))

  const handleTest = async () => {
    const values = await form.validateFields()
    let coli_sn
    try {
      const result = await resolveColiSnAction(values.coli_id.trim())
      coli_sn = result.coli_sn
    } catch (e) {
      message.error(e?.message || '订单号转换失败')
      return
    }
    if (!coli_sn) {
      message.error('未找到订单号对应的 coli_sn')
      return
    }
    const [group, type] = values.template.split('_')
    openPopup(
      `/ht_templates/preview/${values.dei_sn}/${encodeURIComponent(type)}?coli_sn=${coli_sn}&lgc=${values.lgc}&name=${encodeURIComponent(group)}`,
      `ht_tpl_preview_${group}_${type}`,
      { fullscreen: true, left: 0, top: 0 },
    )
  }

  return (
    <div style={{ padding: 12 }}>
      <Card
        size='small'
        title={<span style={{ fontSize: 16, fontWeight: 600 }}>模板测试</span>}
      >
        <Form form={form} layout='inline' initialValues={{ dei_sn: 1, lgc: 1 }} onFinish={handleTest} style={{ rowGap: 12 }}>
          <Form.Item label='模板' name='template' rules={[{ required: true, message: '请选择模板' }]}>
            <Select showSearch optionFilterProp='label' placeholder='选择模板' style={{ width: 240 }} loading={loading} options={TEMPLATE_OPTIONS} />
          </Form.Item>
          <Form.Item label='小组' name='dei_sn' rules={[{ required: true, message: '请选择小组' }]}>
            <Select showSearch optionFilterProp='label' options={DEI_OPTIONS} placeholder='选择小组' style={{ width: 200 }} />
          </Form.Item>
          <Form.Item label='语言' name='lgc' rules={[{ required: true, message: '请选择语言' }]}>
            <Select options={LGC_OPTIONS} style={{ width: 180 }} />
          </Form.Item>
          <Form.Item label='订单coli_id' name='coli_id' rules={[{ required: true, message: '请输入订单号' }]}>
            <Input placeholder='订单号，如 QWX260104140' style={{ width: 180 }} allowClear />
          </Form.Item>
          <Form.Item>
            <Button type='primary' icon={<ExperimentOutlined />} htmlType='submit'>
              测试
            </Button>
          </Form.Item>
        </Form>
        <div style={{ marginTop: 24, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 12, color: '#888' }}>测试案例：</span>
          {TEST_CASES.map((c) => (
            <Button key={c.label} size='small' onClick={() => form.setFieldsValue(c.values)}>{c.label}</Button>
          ))}
        </div>
      </Card>
    </div>
  )
}

export default HtTemplateManager
